import moment from "moment";

interface PerformancePoint {
  timestamp: number;
}

/**
 * Slices a performance series to a time window and anchors both ends.
 *
 * `findIndex` returns -1 when nothing falls inside the window, which used to
 * leave the "first" point undefined: the period delta was then computed
 * against zero and a quiet window reported the whole lifetime profit. With a
 * short window such as the last 24 hours that is the common case, so the
 * anchor is the last point *before* the window instead, giving a flat line and
 * a zero delta.
 */
export function sliceToPeriod<T extends PerformancePoint>(
  history: T[],
  from: number | null | undefined
): { points: T[]; first: T | undefined; last: T | undefined } {
  const last = history[history.length - 1];
  if (from == null || history.length === 0) {
    return { points: history, first: history[0], last };
  }

  const firstInRange = history.findIndex(({ timestamp }) => timestamp >= from);
  // Nothing recorded inside the window: anchor on the last known value.
  const anchor = firstInRange === -1 ? last : (history[firstInRange - 1] ?? history[firstInRange]);
  const inRange = firstInRange === -1 ? [] : history.slice(firstInRange);

  const points = [
    ...(anchor ? [{ ...anchor, timestamp: from }] : []),
    ...inRange,
    ...(last ? [{ ...last, timestamp: moment().valueOf() }] : []),
  ] as T[];

  return { points, first: anchor, last };
}
